// Adapted from Rhine SceneMotion::Spring, smooth and ArchiveField (MIT).
export const smooth = (value) => {
  const t = Math.max(0, Math.min(1, value));
  return t * t * t * (10 + t * (-15 + 6 * t));
};
// Monotonic Hermite tracks and timing from Rhine SceneMotion (MIT).
export function track(keys, time) {
  if (time <= keys[0][0]) return keys[0][1];
  if (time >= keys.at(-1)[0]) return keys.at(-1)[1];
  const slope = i => (keys[i+1][1]-keys[i][1])/(keys[i+1][0]-keys[i][0]);
  const tangent = i => {
    if (!i || i === keys.length-1) return 0;
    const a=slope(i-1), b=slope(i);
    if (a*b<=0) return 0;
    const left=keys[i][0]-keys[i-1][0], right=keys[i+1][0]-keys[i][0];
    return (3*(left+right))/((2*right+left)/a+(right+2*left)/b);
  };
  const i=keys.findIndex(k=>k[0]>=time)-1, span=keys[i+1][0]-keys[i][0];
  const t=(time-keys[i][0])/span, t2=t*t, t3=t2*t;
  return (2*t3-3*t2+1)*keys[i][1]+(t3-2*t2+t)*span*tangent(i)+(-2*t3+3*t2)*keys[i+1][1]+(t3-t2)*span*tangent(i+1);
}
export function decryption(elapsed) {
  const t=34.12+Math.max(0,elapsed)*1.5;
  const grow=track([[34.24,0],[34.4,.19],[34.64,.57],[34.96,.79],[35.28,.92],[35.6,.973],[36.04,1]],t);
  const remaining=track([[37.72,1],[37.88,.72],[38,.38],[38.12,.22],[38.24,.14],[38.4,.075],[38.64,.024],[38.84,0]],t);
  const clarity=track([[38.84,0],[38.92,.28],[39,.51],[39.16,.74],[39.32,.94],[39.56,1]],t);
  return {clarity, intervals: t<34.24 || t>=38.84 ? [] : t<36.04 ? [[0,grow*.5],[1-grow*.5,1]] : [[.5-remaining*.5,.5+remaining*.5]]};
}
const bell = (value, width) => Math.exp(-0.5 * (value / width) ** 2);
// NativeSceneUpdate and SceneLifetimePolicy use exponential damping here,
// independently of the critically damped springs used for extraction.
export function nativeBlend(value, target, rate, seconds, reduced = false) {
  return reduced ? target : value + (target - value) * (1 - Math.exp(-Math.max(0, seconds) * rate));
}
export function returnAngle(value, seconds, reduced = false) {
  const next = value * Math.exp(-Math.max(0, seconds) * (reduced ? 35 : 7));
  return Math.abs(next) <= 0.001 ? 0 : next;
}
export function returnLift(value, seconds, reduced = false) {
  const next = value * Math.exp(-Math.max(0, seconds) * (reduced ? 35 : 4.5));
  return Math.abs(next) < 0.0001 ? 0 : next;
}
export class Spring {
  constructor(value = 0) {
    this.value = value;
    this.velocity = 0;
  }
  step(target, rate, seconds, reduced = false) {
    if (reduced) {
      this.value = target;
      this.velocity = 0;
      return this.value;
    }
    const delta = this.value - target,
      impulse = this.velocity + rate * delta,
      decay = Math.exp(-rate * seconds);
    this.value = target + (delta + impulse * seconds) * decay;
    this.velocity = (this.velocity - rate * impulse * seconds) * decay;
    if (
      Math.abs(this.value - target) < 0.0001 &&
      Math.abs(this.velocity) < 0.0001
    ) {
      this.value = target;
      this.velocity = 0;
    }
    return this.value;
  }
  moving(target) {
    return (
      Math.abs(this.value - target) > 0.0001 || Math.abs(this.velocity) > 0.0001
    );
  }
}
export function archiveField(row, lane, time, pulses = [], idleGain = 0) {
  const age = 1.51 - Math.abs(row) * 0.065;
  const envelope = Math.max(
    -0.42,
    2.15 - 0.17 * (Math.sqrt(row * row + 1) - 1),
  );
  const ring = age > 0 ? Math.sin(age * 5.1) * Math.exp(-age * 1.3) : 0;
  const shoulder =
    envelope *
    (smooth(age / 0.62) + 0.18 * ring * smooth(age / 0.16)) *
    (0.25 + 0.75 * bell(lane, 0.55));
  let ripple = 0;
  for (const pulse of pulses) {
    const elapsed = time - pulse.time;
    if (elapsed < 0 || elapsed > 3.2) continue;
    const distance = Math.hypot(row - pulse.row, lane * 2.2),
      phase = distance - elapsed * 8;
    ripple +=
      0.8 *
      smooth(elapsed / 0.2) *
      Math.exp(-elapsed * 1.15) *
      Math.cos(phase * 0.58) *
      bell(phase, 3.4);
  }
  const breathing = (0.075*Math.sin(time*Math.PI*2/8+row*.3-lane*.45)+0.027*Math.sin(time*Math.PI*2/13-row*.17+lane*.3))*idleGain;
  return shoulder + Math.max(-0.6, Math.min(0.6, ripple)) + breathing;
}
